import Cookies from "js-cookie";
import {CookiesKeysEnum} from "@/lib/enums/CookiesKeysEnum";

/**
 * Cookie-consent state (AC-711).
 *
 * The repo has no consent manager today — Customer.io and GA fire
 * unconditionally. Mixpanel deliberately does NOT copy that behaviour, because
 * enabling analytics before the consent policy is known is a one-way door:
 * data collected without a lawful basis has to be deleted, and the deletion is
 * not something we can do from the frontend.
 *
 * So this module is the gate, and it fails safe. Two modes:
 *
 *  - `required` (DEFAULT) — nothing is tracked until the visitor makes a
 *    decision in the banner. Mixpanel initialises in an opted-out state: no
 *    events, no persistence, no cookies. This is correct for EU/EEA/UK/CH and
 *    California traffic.
 *
 *  - `implied` — track immediately, matching what Customer.io/GA already do,
 *    and show no banner. Only set this for an environment whose traffic is
 *    confirmed to be outside the regulated regions, or once legal has signed
 *    off on a lawful basis. Locally it is the way to work without clicking the
 *    banner on every hard reload.
 *
 * >>> THE SWITCH <<<
 * Set `NEXT_PUBLIC_ANALYTICS_CONSENT_MODE=implied` to opt an environment out of
 * the gate. It is intentionally an explicit, greppable env var rather than a
 * default, so that turning tracking on for real users is a deliberate act by
 * someone who knows the policy — not an accident of configuration.
 *
 * ─── Why categories rather than one boolean ─────────────────────────────────
 * The decision is stored as a per-category record, not `granted | denied`.
 * Persisted consent is effectively immutable: once real visitors carry a value
 * in their browser, changing its shape means either re-prompting everyone or
 * writing a migration for a value the server cannot even read. So the shape
 * that supports the eventual scope — GA, Customer.io marketing sends — is
 * written from the start, even though only `analytics` is wired to anything
 * today.
 *
 * ─── What is actually gated ─────────────────────────────────────────────────
 * TODAY: `analytics` gates Mixpanel, and nothing else. GA and Customer.io still
 * fire regardless of the decision — a known, scoped gap (see the ticket's
 * "Known gap"). `marketing` is stored and gates nothing yet; it exists so the
 * follow-up that attaches Customer.io reads an existing category instead of
 * reworking the storage. This must not ship to EU/EEA/UK traffic until that
 * follow-up lands.
 */

export type ConsentMode = "required" | "implied";

/** The categories the banner offers. `necessary` is not refusable. */
export type ConsentCategory = "necessary" | "analytics" | "marketing";

/**
 * Bumped only if the meaning of the stored categories changes in a way that
 * invalidates an existing decision. A record with an unrecognised version is
 * treated as no decision at all, so the visitor is asked again — fail closed.
 */
export const CONSENT_VERSION = 1;

export interface ConsentRecord {
    v: number;
    /** Always true: strictly necessary cookies (auth, locale) are not refusable. */
    necessary: true;
    /** Gates Mixpanel. */
    analytics: boolean;
    /** Stored, not yet wired to any vendor. */
    marketing: boolean;
}

/** The visitor's answer to the banner — the two categories they can actually choose. */
export type ConsentChoice = Pick<ConsentRecord, "analytics" | "marketing">;

const CONSENT_TTL_DAYS = 365;

export const CONSENT_MODE: ConsentMode =
    process.env.NEXT_PUBLIC_ANALYTICS_CONSENT_MODE === "implied" ? "implied" : "required";

const IMPLIED_RECORD: ConsentRecord = {
    v: CONSENT_VERSION,
    necessary: true,
    analytics: true,
    marketing: true,
};

const isConsentRecord = (value: unknown): value is ConsentRecord => {
    if (typeof value !== "object" || value === null) return false;
    const record = value as Record<string, unknown>;
    return (
        record.v === CONSENT_VERSION &&
        record.necessary === true &&
        typeof record.analytics === "boolean" &&
        typeof record.marketing === "boolean"
    );
};

/**
 * The stored decision, or `null` when the visitor has not been asked yet — which,
 * in `required` mode, means we must not track.
 *
 * A malformed, tampered or outdated cookie is also `null`: an unreadable decision
 * is not a decision, and re-asking is the safe failure.
 */
export const getConsent = (): ConsentRecord | null => {
    if (CONSENT_MODE === "implied") return IMPLIED_RECORD;
    if (typeof window === "undefined") return null;

    const stored = Cookies.get(CookiesKeysEnum.COOKIE_CONSENT);
    if (!stored) return null;

    try {
        const parsed: unknown = JSON.parse(stored);
        return isConsentRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

/** Whether the visitor has answered the banner. Drives whether the banner shows. */
export const hasConsentDecision = (): boolean => getConsent() !== null;

/**
 * The predicate a vendor is gated on. Fails closed: no decision means no consent.
 * `necessary` is always allowed — that is what makes it necessary.
 */
export const isCategoryAllowed = (category: ConsentCategory): boolean => {
    if (category === "necessary") return true;

    const consent = getConsent();
    if (!consent) return false;

    return consent[category];
};

/**
 * The single predicate every Mixpanel call is gated on.
 */
export const isTrackingAllowed = (): boolean => isCategoryAllowed("analytics");

/**
 * Persist the visitor's decision.
 *
 * Prefer `applyConsentDecision` in `lib/analytics/mixpanel.ts` over calling this
 * directly — it wraps this and additionally flips Mixpanel's own opt-in/opt-out
 * state, which is what actually stops or starts the SDK.
 */
export const persistConsent = (choice: ConsentChoice): void => {
    if (typeof window === "undefined") return;

    const record: ConsentRecord = {
        v: CONSENT_VERSION,
        necessary: true,
        analytics: choice.analytics,
        marketing: choice.marketing,
    };

    Cookies.set(CookiesKeysEnum.COOKIE_CONSENT, JSON.stringify(record), {
        expires: CONSENT_TTL_DAYS,
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
    });
};
